// color-exempt-file — retokenisation 5728fe509 : classes dark:bg-[#hex] ajoutees pour preserver le rendu dark historique (voir memoire ui-themes)
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SEOHead } from "../components/SEOHead";
import {
  Check,
  ArrowLeft,
  Crown,
  Sparkles,
  Star,
  Users,
  X,
} from "lucide-react";
import { ProductHeader } from "@core/components/layout/products/ProductHeader";
import { ProductSidebar } from "@core/components/layout/products/ProductSidebar";
import {
  PLANS,
  determineUserPlan,
  CONVERSATION_QUOTAS,
  RETENTION_DAYS,
  MAX_ACTIVE_POLLS,
} from "@core/constants/quotas";
import { buildSoloCheckoutUrl } from "@core/constants/billing";
import { formatCurrency } from "@core/lib/date-utils";
import { useSoloTrial } from "@core/hooks/useSoloTrial";
import { PublicFooter } from "../components/PublicFooter";

const DISCOVERY_CARD_CLASS =
  "flex flex-col w-full sm:w-[340px] bg-gradient-to-br from-slate-800/40 to-slate-900/40 rounded-2xl p-8 border border-slate-700/40 shadow-lg hover:shadow-slate-900/20 transition-shadow"; // color-exempt
const DISCOVERY_ICON_CLASS = "w-8 h-8 text-slate-300"; // color-exempt
const DISCOVERY_PRICE_CLASS = "text-4xl font-bold text-slate-300"; // color-exempt
const DISCOVERY_CTA_CLASS =
  "w-full py-3 px-6 bg-slate-700/40 text-slate-200 rounded-lg font-medium hover:bg-slate-700/60 transition-colors"; // color-exempt

const FREE_CARD_CLASS =
  "flex flex-col w-full sm:w-[340px] bg-gradient-to-br from-blue-50 to-blue-100/60 dark:from-blue-900/20 dark:to-blue-800/20 rounded-2xl p-8 border border-blue-200 dark:border-blue-800/30 shadow-lg hover:shadow-blue-900/10 transition-shadow"; // color-exempt
const FREE_ICON_CLASS = "w-8 h-8 text-blue-600 dark:text-blue-400"; // color-exempt
const FREE_PRICE_CLASS = "text-4xl font-bold text-blue-600 dark:text-blue-400"; // color-exempt
const FREE_CTA_CLASS =
  "w-full py-3 px-6 bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50 rounded-lg font-medium transition-colors"; // color-exempt

// Carte "Solo" — suit --primary du thème actif (bleu en Clair/Sombre,
// corail/indigo sous les 2 nouveaux thèmes) pour que la page réagisse
// vraiment au changement de thème, plutôt qu'un violet fixe historique.
const SOLO_CARD_CLASS =
  "flex flex-col w-full sm:w-[340px] bg-gradient-to-br from-primary/20 to-primary/10 rounded-2xl p-8 border-2 border-primary relative shadow-lg hover:shadow-primary/10 transition-shadow";
const SOLO_ICON_CLASS = "w-8 h-8 text-primary";
const SOLO_PRICE_CLASS = "text-4xl font-bold text-primary";
const SOLO_CHECKBOX_ACCENT_CLASS =
  "mt-0.5 w-3.5 h-3.5 flex-shrink-0 accent-primary cursor-pointer";
const SOLO_LINK_CLASS = "text-primary underline hover:text-primary/80";
const SOLO_CTA_ENABLED_CLASS =
  "bg-primary hover:bg-primary/90 text-primary-foreground cursor-pointer";

export default function DatePollsPricing() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(["polls", "common", "legal"]);
  // Devise selon la locale (fr → EUR, en → USD). LemonSqueezy localise la
  // devise réelle au checkout. NOTE: la valeur USD du tier payant est à
  // confirmer par Julien (pas de conversion FX automatique).
  const lang = i18n.language;
  const currentPlan = "FREE" as "ANONYMOUS" | "FREE" | "SOLO"; // TODO: Get from user profile when auth is implemented
  const hasSoloAccess = currentPlan === "SOLO";
  // L'essai n'est annoncé qu'à qui peut réellement le démarrer. `canStart` est
  // vrai pour un visiteur anonyme (profil nul) comme pour un compte Gratuit
  // n'ayant jamais essayé — les deux publics de cette page — et faux pour un
  // payant ou un essai déjà consommé. Promettre 14 jours à quelqu'un que la
  // base refusera (ALREADY_PAID, TRIAL_ALREADY_USED) est pire que se taire.
  const { canStart: peutEssayer } = useSoloTrial();

  // Renonciation au droit de rétractation — obligation légale art. L221-28 13° du Code de la consommation.
  // L'utilisateur doit consentir explicitement à l'exécution immédiate du service pour perdre son droit de rétractation de 14 jours.
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  const [waiverError, setWaiverError] = useState(false);

  const handleSubscribe = () => {
    if (!waiverAccepted) {
      setWaiverError(true);
      // Scroll vers la case à cocher si non cochée
      document
        .getElementById("withdrawal-waiver")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setWaiverError(false);
    // Traçabilité du consentement (à enregistrer côté serveur au webhook LemonSqueezy via custom_data)
    const consentTimestamp = new Date().toISOString();
    window.location.href = buildSoloCheckoutUrl(consentTimestamp);
  };

  return (
    <div className="min-h-screen-safe bg-background dark:bg-[#030712] text-foreground">
      <SEOHead
        title={t("polls:pricing.seo_title")}
        description={t("polls:pricing.seo_description")}
      />
      <ProductHeader productType="date" />

      <div className="flex">
        <ProductSidebar productType="date" />

        <main className="flex-1 p-8">
          <section className="py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h1 className="text-4xl font-extrabold text-foreground mb-4">
                  {t("polls:pricing.title")}
                </h1>
                <p className="text-xl text-muted-foreground">
                  {t("polls:pricing.subtitle")}
                </p>
              </div>

              <div className="flex flex-wrap justify-center items-stretch gap-8 mx-auto max-w-7xl">
                {/* Anonyme Plan — découverte sans inscription */}
                <div className={DISCOVERY_CARD_CLASS}>
                  <div className="flex items-center justify-center mb-4">
                    <Sparkles className={DISCOVERY_ICON_CLASS} />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground mb-2">
                    {t("polls:pricing.discovery.name")}
                  </h3>
                  <p className="text-muted-foreground mb-4 text-center min-h-[3rem] flex items-center justify-center">
                    {t("polls:pricing.discovery.tagline")}
                  </p>
                  <div className="mb-6">
                    <span className={DISCOVERY_PRICE_CLASS}>
                      {formatCurrency(0, lang)}
                    </span>
                    <span className="text-muted-foreground ml-1 text-sm">
                      {t("polls:pricing.forever")}
                    </span>
                  </div>
                  <ul className="space-y-4 mb-8 flex-grow">
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                      <span className="text-foreground font-medium tracking-wide">
                        {t("polls:pricing.feature.unlimited_manual_creation")}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                      <span className="text-foreground font-medium tracking-wide">
                        {t("polls:pricing.feature.ai_conversations_lifetime", {
                          count: CONVERSATION_QUOTAS.ANONYMOUS,
                        })}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <X className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground font-medium tracking-wide line-through">
                        {t("polls:pricing.feature.dashboard_and_notifications")}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <X className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground font-medium tracking-wide line-through">
                        {t("polls:pricing.feature.edits_after_sending")}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                      <span className="text-foreground font-medium tracking-wide">
                        {t("polls:pricing.feature.exports")}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                      <span className="text-foreground font-medium tracking-wide">
                        {t("polls:pricing.feature.retention_days", {
                          count: RETENTION_DAYS.ANONYMOUS,
                        })}
                      </span>
                    </li>
                  </ul>
                  <button
                    onClick={() => navigate("/create")}
                    className={DISCOVERY_CTA_CLASS}
                  >
                    {t("polls:pricing.discovery.cta")}
                  </button>
                </div>

                {/* Gratuit Plan (connecté) */}
                <div className={FREE_CARD_CLASS}>
                  <div className="flex items-center justify-center mb-4">
                    <Star className={FREE_ICON_CLASS} />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground mb-2">
                    {t("polls:pricing.free.name")}
                  </h3>
                  <p className="text-muted-foreground mb-4 text-center min-h-[3rem] flex items-center justify-center">
                    {t("polls:pricing.free.tagline")}
                  </p>
                  <div className="mb-6">
                    <span className={FREE_PRICE_CLASS}>
                      {formatCurrency(0, lang)}
                    </span>
                    <span className="text-muted-foreground ml-1 text-sm">
                      {t("polls:pricing.forever")}
                    </span>
                  </div>
                  <ul className="space-y-4 mb-8 flex-grow">
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                      <span className="text-foreground font-medium tracking-wide">
                        {t("polls:pricing.feature.unlimited_manual_creation")}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                      <span className="text-foreground font-medium tracking-wide">
                        {t("polls:pricing.feature.ai_conversations_lifetime", {
                          count: CONVERSATION_QUOTAS.FREE,
                        })}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                      <span className="text-foreground font-medium tracking-wide">
                        {t("polls:pricing.feature.full_dashboard")}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                      <span className="text-foreground font-medium tracking-wide">
                        {t("polls:pricing.feature.email_notifications")}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                      <span className="text-foreground font-medium tracking-wide">
                        {t("polls:pricing.feature.max_active_polls", {
                          count: MAX_ACTIVE_POLLS.FREE,
                        })}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                      <span className="text-foreground font-medium tracking-wide">
                        {t("polls:pricing.feature.retention_days", {
                          count: RETENTION_DAYS.FREE,
                        })}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <X className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground font-medium tracking-wide line-through">
                        {t("polls:pricing.feature.ai_fair_use")}
                      </span>
                    </li>
                  </ul>
                  <button
                    onClick={() => navigate("/create")}
                    className={FREE_CTA_CLASS}
                  >
                    {t("polls:pricing.free.cta")}
                  </button>
                </div>

                {/* Solo Plan */}
                <div className={SOLO_CARD_CLASS}>
                  <div className="flex items-center justify-center mb-4">
                    <Crown className={SOLO_ICON_CLASS} />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground mb-2">
                    {t("polls:pricing.solo.name")}
                  </h3>
                  <p className="text-muted-foreground mb-4 text-center min-h-[3rem] flex items-center justify-center">
                    {t("polls:pricing.solo.tagline")}
                  </p>
                  {peutEssayer && (
                    <p
                      className="mb-4 text-center text-sm font-medium text-primary"
                      data-testid="pricing-solo-trial-note"
                    >
                      {t("polls:pricing.solo.trial_note")}{" "}
                      <Link
                        to="/essai"
                        className="underline hover:no-underline"
                        data-testid="pricing-solo-trial-link"
                      >
                        {t("polls:pricing.solo.trial_cta")}
                      </Link>
                    </p>
                  )}
                  <div className="mb-2">
                    <span className={SOLO_PRICE_CLASS}>
                      {formatCurrency(PLANS.SOLO.price, lang)}
                    </span>
                    <span className="text-muted-foreground ml-1">
                      {t("polls:pricing.per_year")}
                    </span>
                  </div>
                  <div className="mb-6"></div>
                  <ul className="space-y-4 mb-8 flex-grow">
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                      <span className="text-foreground font-medium tracking-wide">
                        {t("polls:pricing.feature.ai_fair_use")}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                      <span className="text-foreground font-medium tracking-wide">
                        {t("polls:pricing.feature.unlimited_active_polls")}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                      <span className="text-foreground font-medium tracking-wide">
                        {t("polls:pricing.feature.unlimited_retention")}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                      <span className="text-foreground font-medium tracking-wide">
                        {t("polls:pricing.feature.group_poll_management")}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                      <span className="text-foreground font-medium tracking-wide">
                        {t("polls:pricing.feature.recurring_polls")}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                      <span className="text-foreground font-medium tracking-wide">
                        {t("polls:pricing.feature.early_access_ai")}
                      </span>
                    </li>
                  </ul>
                  {/* Renonciation au droit de rétractation (art. L221-28 13° C. consom.) */}
                  <label
                    id="withdrawal-waiver"
                    className={`flex items-start gap-2 mb-3 cursor-pointer text-[11px] leading-snug rounded-md px-2 py-1.5 border transition-colors ${
                      waiverError
                        ? "border-destructive/70 bg-destructive/20 text-destructive"
                        : "border-border/50 bg-muted/50 text-muted-foreground"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={waiverAccepted}
                      onChange={(e) => {
                        setWaiverAccepted(e.target.checked);
                        if (e.target.checked) setWaiverError(false);
                      }}
                      className={SOLO_CHECKBOX_ACCENT_CLASS}
                      aria-invalid={waiverError}
                    />
                    <span>
                      {t("legal:withdrawal_waiver.consent")}{" "}
                      <Link
                        to="/docs/cg-u"
                        target="_blank"
                        rel="noopener"
                        className={SOLO_LINK_CLASS}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {t("legal:withdrawal_waiver.details")}
                      </Link>
                    </span>
                  </label>
                  <button
                    onClick={handleSubscribe}
                    disabled={!waiverAccepted}
                    className={`w-full py-3 px-6 rounded-lg font-medium transition-all ${
                      waiverAccepted
                        ? SOLO_CTA_ENABLED_CLASS
                        : "bg-muted text-muted-foreground cursor-not-allowed"
                    }`}
                    aria-disabled={!waiverAccepted}
                  >
                    {t("polls:pricing.solo.cta")}
                  </button>
                </div>
              </div>

              {/* Bandeau "Disponible sur mobile" — tous plans confondus */}
              <div className="mt-12 max-w-3xl mx-auto px-6 py-4 rounded-xl border border-border bg-card flex items-center justify-center gap-3 text-sm text-muted-foreground">
                <Check className="w-4 h-4 text-success flex-shrink-0" />
                <span>
                  <strong className="text-foreground">
                    {t("polls:pricing.mobile_banner.title")}
                  </strong>{" "}
                  {t("polls:pricing.mobile_banner.description")}
                </span>
              </div>
            </div>
          </section>

          {/* Features Section */}
          <section className="py-16 border-t border-border">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl font-bold text-center mb-16">
                {t("polls:pricing.why.heading")}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center group">
                  <div className="text-5xl mb-6 group-hover:scale-110 transition-transform">
                    ⚡
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">
                    {t("polls:pricing.why.fast.title")}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {t("polls:pricing.why.fast.description")}
                  </p>
                </div>
                <div className="text-center group">
                  <div className="text-5xl mb-6 group-hover:scale-110 transition-transform">
                    🤝
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">
                    {t("polls:pricing.why.effortless.title")}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {t("polls:pricing.why.effortless.description")}
                  </p>
                </div>
                <div className="text-center group">
                  <div className="text-5xl mb-6 group-hover:scale-110 transition-transform">
                    🎯
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">
                    {t("polls:pricing.why.optimization.title")}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {t("polls:pricing.why.optimization.description")}
                  </p>
                </div>
                <div className="text-center group">
                  <div className="text-5xl mb-6 group-hover:scale-110 transition-transform">
                    📱
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">
                    {t("polls:pricing.why.multidevice.title")}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {t("polls:pricing.why.multidevice.description")}
                  </p>
                </div>
                <div className="text-center group">
                  <div className="text-5xl mb-6 group-hover:scale-110 transition-transform">
                    🔒
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">
                    {t("polls:pricing.why.privacy.title")}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {t("polls:pricing.why.privacy.description")}
                  </p>
                </div>
                <div className="text-center group">
                  <div className="text-5xl mb-6 group-hover:scale-110 transition-transform">
                    📈
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">
                    {t("polls:pricing.why.advanced.title")}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {t("polls:pricing.why.advanced.description")}
                  </p>
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>
      <PublicFooter />
    </div>
  );
}
